/* eslint-disable @typescript-eslint/no-unused-vars */
import { Tooltip } from '@mui/material';
import { GridRenderEditCellParams, GridRowModes, useGridApiContext } from '@mui/x-data-grid-pro';
import { SyntheticEvent, useContext, useEffect, useMemo, useState } from 'react';

import { SelectAutocomplete } from '@shared/inputs/SelectAutocomplete';
import { GridAutocompleteFieldWrapper, GridAutocompleteWrapper } from '@styles/GridStyles';
import { IOption } from '@shared/interfaces';
import { ISelectProps, Select } from '../../inputs/Select';
import { GridRowContext } from './PhoenixGrid';
import { GridCell } from './GridCell';

interface IGridColConditionProps extends Omit<ISelectProps, 'onChange' | 'value'> {
  params: GridRenderEditCellParams;
  isSearchable?: boolean;
  limitTags?: number;
  placeholderOpacity?: number;
  defaultValue?: IOption<unknown>;
  canBeClean?: boolean;
  onChange?: (value: unknown) => void;
}

export const GridColSelect = ({
  params,
  disabled,
  options,
  label,
  limitTags,
  placeholderOpacity,
  onChange,
  canBeClean = true,
  isSearchable = false,
  dense = false,
  multiple = false,
  ...rest
}: IGridColConditionProps) => {
  const api = useGridApiContext();
  const rowContext = useContext(GridRowContext);
  const { id, field, value } = params;
  const [currentValue, setCurrentValue] = useState(value);
  
  useEffect(() => {
    setCurrentValue(value);
  }, [options, value]);

  const isEditMode = api.current.getRowMode(id) === GridRowModes.Edit;

  const handleChange = (value: unknown) => {
    if (rowContext.errors[field]) {
      rowContext.resetError(field);
    }
    setCurrentValue(value);
    api.current.setEditCellValue({ id, field, value });
    onChange?.(value);
  };

  const valueToDisplay = useMemo(() => options.find((option) => option.value === currentValue)?.label || '—', [options]);

  return isEditMode ? (
    <Tooltip followCursor placement="right-end" title={rowContext.errors[field]}>
      <GridAutocompleteFieldWrapper>
        {isSearchable ? (
          <GridAutocompleteWrapper error={Boolean(rowContext.errors[field])}>
            <SelectAutocomplete
              isFitContentWidth
              {...rest}
              canBeClean={canBeClean}
              disabled={disabled}
              options={options}
              value={currentValue}
              error={Boolean(rowContext.errors[field])}
              label={label}
              multiple={multiple}
              limitTags={limitTags}
              placeholderOpacity={placeholderOpacity}
              onChange={handleChange}
            />
          </GridAutocompleteWrapper>
        ) : (
          <Select
            {...rest}
            fullWidth
            dense={dense}
            disabled={disabled}
            options={options}
            value={currentValue}
            error={Boolean(rowContext.errors[field])}
            onChange={handleChange}
          />
        )}
      </GridAutocompleteFieldWrapper>
    </Tooltip>
  ) : (
    <GridCell text={valueToDisplay} />
  );
};
